/**
 * Loading placeholders.
 *
 * The rule for every screen: while a section loads, render a placeholder the
 * same size and shape as the real thing. Sections that render nothing and then
 * appear push the rest of the page down, which makes people tap the wrong
 * button. Reserving the space up front is what prevents that.
 *
 * All of these are decorative, so they are hidden from screen readers; the
 * surrounding section should carry its own aria-busy/loading text if needed.
 */

export function Skeleton({ className = "", rounded = "rounded-md" }) {
  return <div aria-hidden="true" className={`animate-pulse bg-slate-200/80 ${rounded} ${className}`} />
}

// A paragraph of grey lines. The last line is short, like real text.
export function SkeletonText({ lines = 3, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={`h-3 ${index === lines - 1 && lines > 1 ? "w-2/3" : "w-full"}`}
        />
      ))}
    </div>
  )
}

// One bordered card: optional icon square, a title line and body lines.
export function SkeletonCard({ lines = 2, withIcon = false, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
      <div className="flex items-center gap-3">
        {withIcon && <Skeleton className="h-10 w-10 shrink-0" rounded="rounded-xl" />}
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      {lines > 0 && <SkeletonText lines={lines} className="mt-4" />}
    </div>
  )
}

// Tailwind only keeps classes it can see written out in full, so the column
// counts are listed here instead of built from a number at runtime.
const STAT_GRID_COLUMNS = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
}

// Row of summary tiles, e.g. wallet / outstanding / paid.
export function SkeletonStatGrid({ count = 3, className = "" }) {
  return (
    <div className={`grid grid-cols-1 gap-3 ${STAT_GRID_COLUMNS[count] || STAT_GRID_COLUMNS[3]} ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Skeleton className="h-7 w-7" rounded="rounded-lg" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="mt-3 h-7 w-28" />
        </div>
      ))}
    </div>
  )
}

// Table placeholder with a real header strip, so the column widths do not jump
// when the first row of data arrives.
export function SkeletonTable({ rows = 5, columns = 4, className = "" }) {
  return (
    <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="flex gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} className={`h-3 ${index === 1 ? "flex-[2]" : "flex-1"}`} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-3 border-b border-slate-50 px-4 py-3 last:border-b-0">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} className={`h-3.5 ${index === 1 ? "flex-[2]" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  )
}

// Stack of list rows: avatar + two lines + a trailing figure.
export function SkeletonList({ rows = 4, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm">
          <Skeleton className="h-10 w-10 shrink-0" rounded="rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-4 w-16 shrink-0" />
        </div>
      ))}
    </div>
  )
}

// Form placeholder: label + input pairs and a submit button.
export function SkeletonForm({ fields = 4, className = "" }) {
  return (
    <div className={`space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-10 w-full" rounded="rounded-xl" />
        </div>
      ))}
      <Skeleton className="h-11 w-32" rounded="rounded-xl" />
    </div>
  )
}

// Body of an admin page while the signed-in admin is still being checked:
// page title block, then either the form or the table that page shows.
export function SkeletonAdminBody({ form = false, rows = 5, dark = false, className = "" }) {
  const tone = dark ? "bg-white/10" : ""
  return (
    <div className={`mx-auto max-w-5xl px-4 py-6 ${className}`} aria-busy="true" aria-label="Loading">
      <div className="flex items-center gap-3">
        <Skeleton className={`h-10 w-10 shrink-0 ${tone}`} rounded="rounded-xl" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className={`h-5 w-44 ${tone}`} />
          <Skeleton className={`h-3 w-32 ${tone}`} />
        </div>
      </div>
      {form ? <SkeletonForm className="mt-5" /> : <SkeletonTable rows={rows} className="mt-5" />}
    </div>
  )
}

// Whole-page placeholder used while a module's first load runs: the coloured
// header strip every module has, then summary tiles, then the main panel.
export function SkeletonPage({
  stats = 3,
  rows = 5,
  accent = "from-emerald-700 to-teal-700",
  className = "",
}) {
  return (
    <div className={`min-h-screen bg-slate-50 ${className}`}>
      <div className={`bg-gradient-to-r ${accent} px-4 pb-4 pt-3 sm:pt-4`}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
          <Skeleton className="h-9 w-20 bg-white/25" rounded="rounded-lg" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-40 bg-white/25" />
            <Skeleton className="h-3 w-28 bg-white/20" />
          </div>
          <Skeleton className="h-11 w-24 bg-white/25" rounded="rounded-xl" />
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-4 px-4 py-5">
        <SkeletonStatGrid count={stats} />
        <SkeletonTable rows={rows} />
      </div>
    </div>
  )
}

export default Skeleton
